import React, { useRef, useState, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Trash2, 
  Copy, 
  ChevronLeft, 
  ChevronRight, 
  FilePlus, 
  Keyboard, 
  X,
  Layers,
  Sparkles
} from 'lucide-react';
import { DocumentoModelo } from '../../types/documentoModelo';
import { modeloDocumentoService } from '../../services/modeloDocumentoService';
import { renderModeloParaPaginasHtml } from '../../services/modeloRendererService';
import { construirDadosExemplo, validarTokensDoModelo } from '../../services/tokensDocumento';
import { EditorCanvas, EditorCanvasRef } from './EditorCanvas';
import { SidebarElementos } from './SidebarElementos';
import { ToolbarEditor } from './ToolbarEditor';

interface Props {
  modelo: DocumentoModelo;
  onClose: () => void;
  onSaveSuccess?: () => void;
}

export const EditorDocumentoPage: React.FC<Props> = ({ modelo: modeloInicial, onClose, onSaveSuccess }) => {
  const [modelo, setModelo] = useState<DocumentoModelo>(modeloInicial);
  const canvasRef = useRef<EditorCanvasRef | null>(null);

  const [zoomLevel, setZoomLevel] = useState<number>(0.75);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedProps, setSelectedProps] = useState<Record<string, any> | null>(null);
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPaginas, setPreviewPaginas] = useState<string[]>([]);

  // Multi-page state
  const parsePagesFromModel = (canvasJson: string): string[] => {
    try {
      const parsed = typeof canvasJson === 'string' ? JSON.parse(canvasJson) : canvasJson;
      if (parsed && Array.isArray(parsed.pages) && parsed.pages.length > 0) {
        return parsed.pages.map((p: any) => (typeof p === 'string' ? p : JSON.stringify(p)));
      }
    } catch (e) {
      // fallback to single page
    }
    return [canvasJson || JSON.stringify({ version: '5.3.0', objects: [] })];
  };

  const [pages, setPages] = useState<string[]>(() => parsePagesFromModel(modeloInicial.canvas_json));
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);

  // A barra lateral agora é atualizada por evento do canvas (antes havia um
  // setInterval de 150ms rodando o tempo todo, consumindo CPU à toa).

  const handleUpdateModeloName = (nome: string) => {
    setModelo(prev => ({ ...prev, nome }));
    setHasUnsavedChanges(true);
  };

  // Switch Page Handler
  const handleSwitchPage = (newIndex: number) => {
    if (newIndex < 0 || newIndex >= pages.length || newIndex === currentPageIndex) return;
    
    if (canvasRef.current) {
      const currentJson = canvasRef.current.getCanvasJson();
      const updatedPages = [...pages];
      updatedPages[currentPageIndex] = currentJson;
      setPages(updatedPages);
      setCurrentPageIndex(newIndex);
      canvasRef.current.loadCanvasJson(updatedPages[newIndex]);
    } else {
      setCurrentPageIndex(newIndex);
    }
  };

  // Add Page Handler
  const handleAddPage = () => {
    if (!canvasRef.current) return;
    const currentJson = canvasRef.current.getCanvasJson();
    const updatedPages = [...pages];
    updatedPages[currentPageIndex] = currentJson;

    const newPageJson = JSON.stringify({
      version: '5.3.0',
      objects: [
        {
          type: 'i-text',
          left: 50,
          top: 50,
          text: `PÁGINA ${updatedPages.length + 1}`,
          fontSize: 16,
          fontWeight: 'bold',
          fill: '#0f172a',
          fontFamily: 'Arial'
        }
      ]
    });

    updatedPages.push(newPageJson);
    setPages(updatedPages);
    const newIdx = updatedPages.length - 1;
    setCurrentPageIndex(newIdx);
    canvasRef.current.loadCanvasJson(newPageJson);
    setHasUnsavedChanges(true);
  };

  // Duplicate Page Handler
  const handleDuplicatePage = () => {
    if (!canvasRef.current) return;
    const currentJson = canvasRef.current.getCanvasJson();
    const updatedPages = [...pages];
    updatedPages[currentPageIndex] = currentJson;
    updatedPages.splice(currentPageIndex + 1, 0, currentJson);

    setPages(updatedPages);
    const newIdx = currentPageIndex + 1;
    setCurrentPageIndex(newIdx);
    canvasRef.current.loadCanvasJson(currentJson);
    setHasUnsavedChanges(true);
  };

  // Delete Page Handler
  const handleDeletePage = (pageIdx: number) => {
    if (pages.length <= 1) {
      alert('O documento precisa ter pelo menos 1 página.');
      return;
    }
    if (!confirm(`Deseja excluir a Página ${pageIdx + 1}?`)) return;

    const updatedPages = pages.filter((_, idx) => idx !== pageIdx);
    const nextIdx = Math.min(pageIdx, updatedPages.length - 1);

    setPages(updatedPages);
    setCurrentPageIndex(nextIdx);
    if (canvasRef.current) {
      canvasRef.current.loadCanvasJson(updatedPages[nextIdx]);
    }
    setHasUnsavedChanges(true);
  };

  /** Monta o JSON completo do modelo com o estado atual do canvas (todas as páginas). */
  const montarCanvasJsonAtual = (): { json: string; paginas: string[] } => {
    const updatedPages = [...pages];
    if (canvasRef.current) {
      updatedPages[currentPageIndex] = canvasRef.current.getCanvasJson();
    }
    return {
      json: JSON.stringify({
        version: '5.3.0',
        paginas_count: updatedPages.length,
        pages: updatedPages
      }),
      paginas: updatedPages
    };
  };

  // Fechar com alterações não salvas pede confirmação (antes o trabalho era
  // simplesmente perdido, mesmo com o editor sabendo que havia alterações).
  const handleCloseSeguro = () => {
    if (hasUnsavedChanges) {
      const confirmar = confirm(
        'Você tem alterações não salvas neste modelo.\n\nFechar agora vai descartar essas alterações. Deseja continuar?'
      );
      if (!confirmar) return;
    }
    onClose();
  };

  // Pré-visualização preenchida com dados de exemplo
  const handleAbrirPreview = () => {
    const { json, paginas } = montarCanvasJsonAtual();
    const modeloPreview: DocumentoModelo = {
      ...modelo,
      canvas_json: json,
      paginas_count: paginas.length
    };

    const itensExemplo = [
      { descricao: 'Transporte dedicado - VUC Baú 3.5T', quantidade: '2 VJ', valorUnitario: 'R$ 750,00', valorTotal: 'R$ 1.500,00' },
      { descricao: 'Caminhão Truck 23T - Rota interestadual', quantidade: '1 VJ', valorUnitario: 'R$ 2.300,00', valorTotal: 'R$ 2.300,00' },
      { descricao: 'Ajudante para carga e descarga', quantidade: '3 DIÁRIA', valorUnitario: 'R$ 180,00', valorTotal: 'R$ 540,00' }
    ];

    setPreviewPaginas(renderModeloParaPaginasHtml(modeloPreview, construirDadosExemplo(), itensExemplo));
    setIsPreviewOpen(true);
  };

  // Save Handler
  const handleSave = async () => {
    if (!canvasRef.current) return;

    const { json: fullCanvasJsonPrevio } = montarCanvasJsonAtual();

    // Avisa se o modelo usa campos que o sistema não sabe preencher — senão o
    // token cru acabaria impresso no documento enviado ao cliente.
    const { tokensInvalidos, tokensIndisponiveis } = validarTokensDoModelo(fullCanvasJsonPrevio, modelo.tipo);
    if (tokensInvalidos.length > 0 || tokensIndisponiveis.length > 0) {
      const linhas: string[] = [];
      if (tokensInvalidos.length > 0) {
        linhas.push(`Campos desconhecidos (provável erro de digitação):\n  {{${tokensInvalidos.join('}}, {{')}}}`);
      }
      if (tokensIndisponiveis.length > 0) {
        linhas.push(`Campos sem dado neste tipo de documento:\n  {{${tokensIndisponiveis.join('}}, {{')}}}`);
      }
      const confirmar = confirm(
        `Atenção:\n\n${linhas.join('\n\n')}\n\nEsses campos sairão em branco no documento gerado. Deseja salvar mesmo assim?`
      );
      if (!confirmar) return;
    }

    setIsSaving(true);
    try {
      const currentJson = canvasRef.current.getCanvasJson();
      const updatedPages = [...pages];
      updatedPages[currentPageIndex] = currentJson;

      const fullCanvasJson = JSON.stringify({
        version: '5.3.0',
        paginas_count: updatedPages.length,
        pages: updatedPages
      });

      const updated = await modeloDocumentoService.salvarModelo({
        ...modelo,
        paginas_count: updatedPages.length,
        canvas_json: fullCanvasJson,
      });

      setModelo(updated);
      setPages(updatedPages);
      setHasUnsavedChanges(false);
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      console.error('Erro ao salvar modelo:', err);
      alert('Erro ao salvar o modelo de documento.');
    } finally {
      setIsSaving(false);
    }
  };

  // Global Keyboard Shortcuts Listener
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Check if active element is input, textarea or editable
    const target = e.target as HTMLElement | null;
    const isEditingInput = target && (
      target.tagName === 'INPUT' || 
      target.tagName === 'TEXTAREA' || 
      target.isContentEditable
    );

    // Also check if fabric canvas text object is being edited
    const isEditingFabricText = canvasRef.current?.isEditingText();

    const isCtrlOrCmd = e.ctrlKey || e.metaKey;

    // Ctrl + S (Save) - works even if typing
    if (isCtrlOrCmd && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      handleSave();
      return;
    }

    if (isEditingInput || isEditingFabricText) return;

    // Ctrl + Z (Undo)
    if (isCtrlOrCmd && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      canvasRef.current?.undo();
      return;
    }

    // Ctrl + Y or Ctrl + Shift + Z (Redo)
    if ((isCtrlOrCmd && (e.key === 'y' || e.key === 'Y')) || (isCtrlOrCmd && e.shiftKey && (e.key === 'z' || e.key === 'Z'))) {
      e.preventDefault();
      canvasRef.current?.redo();
      return;
    }

    // Delete / Backspace
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      canvasRef.current?.deleteSelected();
      return;
    }

    // Ctrl + C (Copy)
    if (isCtrlOrCmd && (e.key === 'c' || e.key === 'C')) {
      e.preventDefault();
      canvasRef.current?.copySelected();
      return;
    }

    // Ctrl + V (Paste)
    if (isCtrlOrCmd && (e.key === 'v' || e.key === 'V')) {
      e.preventDefault();
      canvasRef.current?.pasteSelected();
      return;
    }

    // Ctrl + D (Duplicate)
    if (isCtrlOrCmd && (e.key === 'd' || e.key === 'D')) {
      e.preventDefault();
      canvasRef.current?.duplicateSelected();
      return;
    }

    // Ctrl + A (Select All)
    if (isCtrlOrCmd && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      canvasRef.current?.selectAll();
      return;
    }

    // Arrow Keys (Nudge 1px or 10px with Shift)
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      let dx = 0;
      let dy = 0;
      if (e.key === 'ArrowUp') dy = -step;
      if (e.key === 'ArrowDown') dy = step;
      if (e.key === 'ArrowLeft') dx = -step;
      if (e.key === 'ArrowRight') dx = step;
      canvasRef.current?.nudgeSelected(dx, dy);
      return;
    }

    // Zoom shortcuts
    if (isCtrlOrCmd && (e.key === '=' || e.key === '+')) {
      e.preventDefault();
      setZoomLevel(prev => Math.min(2.0, prev + 0.1));
      return;
    }
    if (isCtrlOrCmd && (e.key === '-' || e.key === '_')) {
      e.preventDefault();
      setZoomLevel(prev => Math.max(0.3, prev - 0.1));
      return;
    }
    if (isCtrlOrCmd && e.key === '0') {
      e.preventDefault();
      setZoomLevel(0.75);
      return;
    }

    // Escape (Deselect)
    if (e.key === 'Escape') {
      e.preventDefault();
      canvasRef.current?.clearSelection();
      return;
    }

    // Shift + ? (Open Shortcuts Modal)
    if (e.shiftKey && e.key === '?') {
      e.preventDefault();
      setIsShortcutsModalOpen(true);
      return;
    }
  }, [pages, currentPageIndex, modelo]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col w-screen h-screen overflow-hidden select-none">
      {/* Top Bar / Toolbar */}
      <ToolbarEditor
        modelo={modelo}
        onUpdateModeloName={handleUpdateModeloName}
        onUndo={() => canvasRef.current?.undo()}
        onRedo={() => canvasRef.current?.redo()}
        onDuplicate={() => canvasRef.current?.duplicateSelected()}
        onDelete={() => canvasRef.current?.deleteSelected()}
        onBringToFront={() => canvasRef.current?.bringToFront()}
        onSendToBack={() => canvasRef.current?.sendToBack()}
        onBringForward={() => canvasRef.current?.bringForward()}
        onSendBackward={() => canvasRef.current?.sendBackward()}
        zoomLevel={zoomLevel}
        onZoomIn={() => setZoomLevel(prev => Math.min(2.0, prev + 0.1))}
        onZoomOut={() => setZoomLevel(prev => Math.max(0.3, prev - 0.1))}
        onResetZoom={() => setZoomLevel(0.75)}
        onSave={handleSave}
        onClose={handleCloseSeguro}
        onOpenShortcutsModal={() => setIsShortcutsModalOpen(true)}
        isSaving={isSaving}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Sidebar Elementos (Left) */}
        <SidebarElementos
          onAddText={(text, options) => canvasRef.current?.addText(text, options)}
          onAddRect={() => canvasRef.current?.addRect()}
          onAddCircle={() => canvasRef.current?.addCircle()}
          onAddLine={() => canvasRef.current?.addLine()}
          onAddImage={(url) => canvasRef.current?.addImage(url)}
          selectedProps={selectedProps}
          onUpdateStyle={(styleProps) => canvasRef.current?.updateSelectedStyle(styleProps)}
        />

        {/* Center Canvas Area with Page Navigation Bar */}
        <div className="flex-1 flex flex-col overflow-hidden bg-slate-950/60 relative">
          {/* Multi-Page Management Bar */}
          <div className="h-11 bg-slate-900/90 border-b border-slate-800 px-4 flex items-center justify-between shrink-0 shadow-sm z-10">
            {/* Left: Page Switcher Tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none py-1">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-2 flex items-center gap-1">
                <Layers className="w-3.5 h-3.5 text-red-500" />
                Páginas ({pages.length}):
              </span>

              {pages.map((_, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSwitchPage(idx)}
                  className={`group relative px-3 py-1 rounded-lg text-xs font-bold cursor-pointer transition flex items-center gap-1.5 border ${
                    currentPageIndex === idx
                      ? 'bg-red-600 text-white border-red-500 shadow-sm'
                      : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 border-slate-700/60'
                  }`}
                >
                  <span>Página {idx + 1}</span>
                  {pages.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePage(idx);
                      }}
                      className={`p-0.5 rounded hover:bg-black/30 transition opacity-60 hover:opacity-100 ${
                        currentPageIndex === idx ? 'text-white' : 'text-slate-400 hover:text-rose-400'
                      }`}
                      title={`Excluir Página ${idx + 1}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Right: Page Actions (Add, Duplicate, Prev/Next) */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleAbrirPreview}
                className="px-2.5 py-1 bg-emerald-600/90 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold flex items-center gap-1 transition active:scale-95 shadow-sm"
                title="Ver como o documento fica preenchido com dados reais"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Visualizar Preenchido</span>
              </button>

              <button
                onClick={handleDuplicatePage}
                className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-semibold flex items-center gap-1 border border-slate-700/60 transition active:scale-95"
                title="Duplicar a página atual"
              >
                <Copy className="w-3.5 h-3.5 text-blue-400" />
                <span className="hidden sm:inline">Duplicar Página</span>
              </button>

              <button
                onClick={handleAddPage}
                className="px-3 py-1 bg-red-600/90 hover:bg-red-600 text-white rounded-lg text-xs font-bold flex items-center gap-1 transition active:scale-95 shadow-sm"
                title="Adicionar uma nova página em branco ao modelo"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>+ Adicionar Página</span>
              </button>

              <div className="h-4 w-px bg-slate-800 mx-1" />

              <div className="flex items-center gap-1 bg-slate-800/80 p-0.5 rounded-lg border border-slate-700/60">
                <button
                  onClick={() => handleSwitchPage(currentPageIndex - 1)}
                  disabled={currentPageIndex === 0}
                  className="p-1 rounded hover:bg-slate-700 text-slate-300 disabled:opacity-30 disabled:hover:bg-transparent transition"
                  title="Página Anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-[11px] font-mono font-bold text-slate-300 px-1">
                  {currentPageIndex + 1}/{pages.length}
                </span>
                <button
                  onClick={() => handleSwitchPage(currentPageIndex + 1)}
                  disabled={currentPageIndex === pages.length - 1}
                  className="p-1 rounded hover:bg-slate-700 text-slate-300 disabled:opacity-30 disabled:hover:bg-transparent transition"
                  title="Próxima Página"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Canvas Workspace */}
          <EditorCanvas
            ref={canvasRef}
            initialJson={pages[currentPageIndex]}
            onChange={() => setHasUnsavedChanges(true)}
            zoomLevel={zoomLevel}
            onSelectionChange={setSelectedProps}
          />
        </div>
      </div>

      {/* Pré-visualização preenchida com dados de exemplo */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 bg-slate-900 border-b border-slate-800 shrink-0">
            <div className="flex items-center gap-2.5 text-white">
              <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-xl border border-emerald-500/30">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Pré-visualização com dados de exemplo</h3>
                <p className="text-xs text-slate-400">
                  É assim que o documento sai preenchido. Campos sem dado aparecem em branco.
                </p>
              </div>
            </div>

            <button
              onClick={() => setIsPreviewOpen(false)}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition"
            >
              <X className="w-4 h-4" />
              Fechar
            </button>
          </div>

          <div className="flex-1 overflow-auto p-6 flex flex-col items-center gap-6">
            {previewPaginas.map((html, idx) => (
              <div key={idx} className="shrink-0">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">
                  Página {idx + 1}
                </span>
                <div
                  className="bg-white shadow-2xl relative overflow-hidden"
                  style={{ width: `${modelo.largura_px || 794}px`, height: `${modelo.altura_px || 1123}px` }}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help Modal */}
      {isShortcutsModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full p-6 text-white shadow-2xl space-y-5 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
                  <Keyboard className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base">Atalhos do Teclado</h3>
                  <p className="text-xs text-slate-400">Guia de navegação rápida no editor visual Canva VOOPE</p>
                </div>
              </div>

              <button
                onClick={() => setIsShortcutsModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Salvar Modelo</span>
                <kbd className="px-2 py-1 bg-slate-900 text-amber-400 rounded font-mono font-bold text-[11px] border border-slate-700">Ctrl + S</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Desfazer (Undo)</span>
                <kbd className="px-2 py-1 bg-slate-900 text-slate-200 rounded font-mono font-bold text-[11px] border border-slate-700">Ctrl + Z</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Refazer (Redo)</span>
                <kbd className="px-2 py-1 bg-slate-900 text-slate-200 rounded font-mono font-bold text-[11px] border border-slate-700">Ctrl + Y</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Excluir Item</span>
                <kbd className="px-2 py-1 bg-slate-900 text-rose-400 rounded font-mono font-bold text-[11px] border border-slate-700">Del / Backspace</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Copiar Elemento</span>
                <kbd className="px-2 py-1 bg-slate-900 text-slate-200 rounded font-mono font-bold text-[11px] border border-slate-700">Ctrl + C</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Colar Elemento</span>
                <kbd className="px-2 py-1 bg-slate-900 text-slate-200 rounded font-mono font-bold text-[11px] border border-slate-700">Ctrl + V</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Duplicar Elemento</span>
                <kbd className="px-2 py-1 bg-slate-900 text-blue-400 rounded font-mono font-bold text-[11px] border border-slate-700">Ctrl + D</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Mover Elemento</span>
                <kbd className="px-2 py-1 bg-slate-900 text-slate-200 rounded font-mono font-bold text-[11px] border border-slate-700">Setas (Shift + Setas)</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Aumentar Zoom</span>
                <kbd className="px-2 py-1 bg-slate-900 text-slate-200 rounded font-mono font-bold text-[11px] border border-slate-700">Ctrl + +</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Reduzir Zoom</span>
                <kbd className="px-2 py-1 bg-slate-900 text-slate-200 rounded font-mono font-bold text-[11px] border border-slate-700">Ctrl + -</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Resetar Zoom</span>
                <kbd className="px-2 py-1 bg-slate-900 text-slate-200 rounded font-mono font-bold text-[11px] border border-slate-700">Ctrl + 0</kbd>
              </div>

              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700/50 flex items-center justify-between">
                <span className="text-slate-300 font-medium">Deselecionar</span>
                <kbd className="px-2 py-1 bg-slate-900 text-slate-200 rounded font-mono font-bold text-[11px] border border-slate-700">Esc</kbd>
              </div>
            </div>

            <div className="pt-2 flex justify-end border-t border-slate-800">
              <button
                onClick={() => setIsShortcutsModalOpen(false)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs transition"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
